import {
  Card,
  CardBody,
  Copy,
  Icon,
  ProgressBar,
  Row,
  Stretch,
  Title,
  Wrapper,
  Popover
} from "app/NativeComponents/common";
import { CardLabel } from "app/NativeComponents/snippets";
import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import api from "app/DealMachineCore/apis/DealMachineAPIV2";
import { useAsyncMemo } from "app/DealMachineCore/hooks/useAsyncMemo";
import { formatDate } from "app/NativeActions";

const dm_api = api.create();
export const defaultAffiliateData = {
  partner_info: {},
  tier_info: {},
  report: []
};

const TIERS = [
  {
    id: "1",
    name: "Silver",
    description: null,
    point_range_min: 0,
    point_range_max: 99,
    revenue_percentage: 0.25
  },
  {
    id: "2",
    name: "Gold",
    description: null,
    point_range_min: 100,
    point_range_max: 499,
    revenue_percentage: 0.4
  },
  {
    id: "3",
    name: "Diamond",
    description: null,
    point_range_min: 500,
    point_range_max: -1,
    revenue_percentage: 0.5
  }
];

const clamp = (num, min, max) => (num <= min ? min : num >= max ? max : num);

const PartnerMetrics = () => {
  const colors = useSelector(state => state.settings.colors);
  const { token } = useSelector(state => state.auth);
  const [loading, setLoading] = useState(false);
  const affiliateData = useAsyncMemo(
    async () => {
      if (loading || !token) return defaultAffiliateData;
      setLoading(true);
      const response = await dm_api.getAffiliateDashboard({ token });
      setLoading(false);
      if (!response.data || response.data.error || !response.data.results)
        return defaultAffiliateData;
      return response.data.results;
    },
    [token],
    defaultAffiliateData
  );
  const points = useMemo(() => {
    if (!affiliateData.tier_info) return 0;
    return affiliateData.tier_info.points;
  }, [affiliateData]);
  const tierStatus = useMemo(() => {
    if (!affiliateData.tier_info || !affiliateData.tier_info.tier) return {};
    const currentTier =
      TIERS.find(t => t.id == affiliateData.tier_info.tier.tier_id) || {};
    return { ...affiliateData.tier_info.tier, currentTier };
  }, [affiliateData]);
  const nextTier = useMemo(() => {
    if (points < TIERS[1].point_range_min) {
      return {
        name: TIERS[1].name,
        pointsToPromotion: TIERS[1].point_range_min - points
      };
    }
    if (points < TIERS[2].point_range_min) {
      return {
        name: TIERS[2].name,
        pointsToPromotion: TIERS[2].point_range_min - points
      };
    }
    return { maxed: true };
  }, [points, TIERS]);

  return (
    <Wrapper>
      <Card>
        {tierStatus.below_threshold_at && (
          <CardLabel
            title={`You are currently below your tier's minimum point threshold. To remain on the ${
              tierStatus.currentTier.name
            } tier you must earn more points before ${formatDate(
              tierStatus.below_threshold_at,
              date => new Date(date.setMonth(date.getMonth() + 1))
            )}.`}
            icon={"warning"}
            hasButton={false}
            onPress={() => {}}
            hasBorder={true}
          />
        )}
        <CardBody>
          <Stretch>
            <Wrapper style={{ width: "100%" }}>
              <Row style={{ justifyContent: "space-between" }}>
                <Wrapper>
                  <Title style={{ color: colors.green_color }}>
                    {points} points
                  </Title>
                  {!nextTier.maxed && (
                    <Copy style={{ marginRight: 10, lineHeight: "28px" }}>
                      {nextTier.pointsToPromotion} points to {nextTier.name}
                    </Copy>
                  )}
                </Wrapper>
                <Wrapper>
                  <Popover
                    visible={true}
                    overlayStyle={{ width: "400px" }}
                    overlayContent={
                      <Wrapper>
                        <CardLabel
                          title={"Earn points for your sign ups!"}
                          icon={"check"}
                          hasButton={false}
                          onPress={() => {}}
                          hasBorder={true}
                        />
                        <CardBody>
                          <Row
                            style={{
                              justifyContent: "space-between",
                              marginBottom: 20
                            }}
                          >
                            <Title>Sign up date</Title>
                            <Title>Points</Title>
                          </Row>
                          <Row
                            style={{
                              justifyContent: "space-between",
                              marginBottom: 15
                            }}
                          >
                            <Copy>Less than 3 months ago</Copy>
                            <Copy>1</Copy>
                          </Row>
                          <Row
                            style={{
                              justifyContent: "space-between",
                              marginBottom: 15
                            }}
                          >
                            <Copy>Less than 6 months ago</Copy>
                            <Copy>0.5</Copy>
                          </Row>
                          <Row
                            style={{
                              justifyContent: "space-between",
                              marginBottom: 20
                            }}
                          >
                            <Copy>Less than 12 months ago</Copy>
                            <Copy>0.25</Copy>
                          </Row>
                          <Copy style={{ color: colors.light_text_color }}>
                            Example: A user that signed up 4 months ago using
                            your promo code will be worth 0.5 points, but a user
                            that signed up 1 month ago will be worth 1 point.
                          </Copy>
                        </CardBody>
                      </Wrapper>
                    }
                  >
                    <Icon icon={"info"} size={24} />
                  </Popover>
                </Wrapper>
              </Row>
              <Row style={{ margin: "10px 0" }}>
                <Icon
                  icon={
                    points >= TIERS[0].point_range_min &&
                    !(points >= TIERS[1].point_range_min)
                      ? "radio_button_checked"
                      : "radio_button_unchecked"
                  }
                  color={colors.green_color}
                  style={{ marginRight: "-4px", zIndex: "1" }}
                  size={28}
                />
                <ProgressBar
                  color={colors.green_color}
                  height={10}
                  style={{
                    width: "33%",
                    margin: 0,
                    overflow: "hidden"
                  }}
                  progress={(points || 0) / TIERS[1].point_range_min}
                />
                <Icon
                  icon={
                    points >= TIERS[1].point_range_min &&
                    !(points >= TIERS[2].point_range_min)
                      ? "radio_button_checked"
                      : "radio_button_unchecked"
                  }
                  color={
                    points >= TIERS[1].point_range_min
                      ? colors.green_color
                      : "#eee"
                  }
                  style={{ marginLeft: "-4px", zIndex: "1" }}
                  size={28}
                />
                <ProgressBar
                  color={colors.green_color}
                  height={10}
                  style={{ width: "67%", margin: 0, marginLeft: "-4px" }}
                  progress={
                    (points
                      ? clamp(
                          points - TIERS[1].point_range_min,
                          0,
                          TIERS[2].point_range_min - TIERS[1].point_range_min
                        )
                      : 0) /
                    (TIERS[2].point_range_min - TIERS[1].point_range_min)
                  }
                />
                <Icon
                  icon={
                    points >= TIERS[2].point_range_min
                      ? "radio_button_checked"
                      : "radio_button_unchecked"
                  }
                  color={
                    points >= TIERS[2].point_range_min
                      ? colors.green_color
                      : "#eee"
                  }
                  style={{ marginLeft: "-4px" }}
                  size={28}
                />
              </Row>
              <Row style={{ justifyContent: "space-between" }}>
                <Row style={{ width: "52%", justifyContent: "space-between" }}>
                  <Wrapper>
                    <Title style={{ color: colors.green_color }}>SILVER</Title>
                    <Copy>0-99 points</Copy>
                    <Copy>25% revenue share</Copy>
                  </Wrapper>
                  <Wrapper>
                    <Title style={{ color: colors.green_color }}>GOLD</Title>
                    <Copy>100-499 points</Copy>
                    <Copy>40% revenue share</Copy>
                  </Wrapper>
                </Row>
                <Row>
                  <Wrapper>
                    <Title style={{ color: colors.green_color }}>DIAMOND</Title>
                    <Copy>500+ points</Copy>
                    <Copy>50% revenue share</Copy>
                  </Wrapper>
                </Row>
              </Row>
            </Wrapper>
          </Stretch>
        </CardBody>
      </Card>
    </Wrapper>
  );
};

export default PartnerMetrics;
